import React, { useEffect, useState, useMemo } from 'react';
import { fetchAdminUsersCached } from '../services/users';
import AdminLayout from '../components/AdminLayout';
import Modal from '../components/Modal';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus('loading');
      setError('');
      try {
        const list = await fetchAdminUsersCached();
        if (!cancelled) setUsers(list);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load users');
      } finally {
        if (!cancelled) setStatus('idle');
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const uid = (u.uid || u.id || '').toLowerCase();
      return name.includes(q) || email.includes(q) || uid.includes(q);
    });
  }, [users, search]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const getVal = (u) => {
      switch (sortKey) {
        case 'name':
          return (u.name || '').toLowerCase();
        case 'email':
          return (u.email || '').toLowerCase();
        case 'uid':
          return (u.uid || u.id || '').toLowerCase();
        case 'premium': {
          const m = getMembership(u.premium);
          const enabled = typeof m.enabled === 'boolean' ? m.enabled : (typeof u.premium === 'boolean' ? u.premium : false);
          return enabled ? 1 : 0;
        }
        case 'createdAt':
        default: {
          const v = u.createdAt;
          return v?.toMillis ? v.toMillis() : (typeof v === 'number' ? v : (typeof v === 'string' ? Date.parse(v) || 0 : 0));
        }
      }
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <AdminLayout>
      <div className="content-card">
        <h2 className="section-title">Users</h2>
        <p className="section-subtitle">Aggregated users and activity
          {status === 'loading' ? ' — Loading…' : ''}
        </p>
        {error && <p style={{ color: '#c0392b' }}>{error}</p>}
          <div className="table-card" style={{ marginTop: 16 }}>
          <div className="table-toolbar">
            <div className="right">
              <input
                className="input"
                type="text"
                placeholder="Search by name, email, or UID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ minWidth: 260 }}
              />
            </div>
          </div>
          <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button type="button" onClick={() => toggleSort('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Name {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('email')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Email {sortKey === 'email' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('uid')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    UID {sortKey === 'uid' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('premium')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Premium {sortKey === 'premium' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th>
                  <button type="button" onClick={() => toggleSort('createdAt')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    Joined {sortKey === 'createdAt' ? (sortDir === 'asc' ? '▲' : '▼') : '↕︎'}
                  </button>
                </th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => (
                <tr key={u.uid || u.id}>
                  <td>{u.name || '—'}</td>
                  <td>{u.email || '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>{u.uid || u.id || '—'}</td>
                  <td>{(typeof getMembership(u.premium).enabled === 'boolean' ? getMembership(u.premium).enabled : (typeof u.premium === 'boolean' ? u.premium : false)) ? 'Yes' : 'No'}</td>
                  <td>{formatDate(u.createdAt)}</td>
                  <td>
                    <button
                      className="btn btn-pill btn-dark"
                      type="button"
                      onClick={() => setSelected(u)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        <Modal
          open={!!selected}
          title={selected ? (selected.name || selected.email || selected.uid || 'User Details') : 'User Details'}
          onClose={() => setSelected(null)}
          footer={
            <button className="btn btn-pill" onClick={() => setSelected(null)} data-autofocus>
              Close
            </button>
          }
        >
          {selected && (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8 }}>
                <div style={{ color: 'var(--text-secondary)' }}>Name</div>
                <div>{selected.name || '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Email</div>
                <div>{selected.email || '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>UID</div>
                <div style={{ fontFamily: 'monospace' }}>{selected.uid || selected.id || '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Joined</div>
                <div>{formatDate(selected.createdAt)}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Membership</div>
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 6 }}>
                    <div style={{ color: 'var(--text-secondary)' }}>Status</div>
                    <div>{formatMembershipEnabled(selected.premium)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Plan</div>
                    <div>{formatMembershipPlan(selected.premium)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Started</div>
                    <div>{formatMembershipStart(selected.premium)}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>Ends</div>
                    <div>{formatMembershipEnd(selected.premium)}</div>
                  </div>
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>Notifications</div>
                <div>{selected.notificationsMuted ? 'Muted' : 'Active'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Streak</div>
                <div>{Number.isFinite(selected.streakCount) ? selected.streakCount : '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Last Day</div>
                <div>{selected.streakLastDay ? formatDate(selected.streakLastDay) : '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Longest Streak</div>
                <div>{Number.isFinite(selected.longestStreak) ? selected.longestStreak : '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Badges</div>
                <div>{Number.isFinite(selected.badgesCount) ? selected.badgesCount : '—'}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Own Tasks</div>
                <div>{selected.ownTasksCount ?? 0}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Favorites</div>
                <div>{selected.favoritesCount ?? 0}</div>
                <div style={{ color: 'var(--text-secondary)' }}>Collections</div>
                <div>{selected.collectionsCount ?? 0}</div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>
  );
}

function formatDate(v) {
  if (!v) return '—';
  try {
    const date =
      v?.toMillis ? new Date(v.toMillis()) :
      typeof v === 'number' ? new Date(v) :
      typeof v === 'string' ? new Date(v) : null;

    if (!date || isNaN(date.getTime())) return String(v);

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatPremium(p) {
  if (p == null) return '—';
  if (typeof p === 'boolean') return p ? 'Yes' : 'No';
  if (typeof p === 'object') {
    const plan = p.plan || '—';
    const until = p.until ? formatDate(p.until) : '—';
    return `${plan} (until ${until})`;
  }
  return String(p);
}

function getMembership(p) {
  if (p == null) return { enabled: null, plan: null, start: null, end: null };
  if (typeof p === 'boolean') return { enabled: p, plan: null, start: null, end: null };
  if (typeof p === 'object') {
    return {
      enabled: typeof p.enabled === 'boolean' ? p.enabled : null,
      plan: p.plan || null,
      start: p.start || null,
      end: p.end || p.until || null,
    };
  }
  return { enabled: null, plan: null, start: null, end: null };
}

function formatMembershipEnabled(p) {
  const m = getMembership(p);
  if (m.enabled == null) return '—';
  return m.enabled ? 'Active' : 'Inactive';
}

function formatMembershipPlan(p) {
  const m = getMembership(p);
  return m.plan || '—';
}

function formatMembershipStart(p) {
  const m = getMembership(p);
  return m.start ? formatDate(m.start) : '—';
}

function formatMembershipEnd(p) {
  const m = getMembership(p);
  return m.end ? formatDate(m.end) : '—';
}